import { Box, ErrorBoundary } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import React from 'react';

import { reportGeneratorListLayout } from '../../../constants/table';
import { reportGeneratorTheme } from '../../../constants/theme';

import { DocumentsTableList } from './shared/DocumentsTableList';
import { DocumentsTableSkeletonList } from './shared/DocumentsTableSkeletonList';
import { DocumentsTableTableLayout } from './table';
import { ReportGeneratorListingProps } from './types';

interface DocumentsTableProps {
  listing: ReportGeneratorListingProps;
}

const DocumentsTableCardContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    width="full"
    bg={reportGeneratorTheme.table.historyBg}
    borderRadius={reportGeneratorTheme.table.frameBorderRadius}
    p="inline.quarck">
    <Box
      width="full"
      bg={reportGeneratorTheme.table.rowBg}
      borderRadius={reportGeneratorTheme.table.listPanelBorderRadius}
      borderWidth={1}
      borderColor={reportGeneratorTheme.table.listPanelBorderColor}
      py="inline.xxxs">
      {children}
    </Box>
  </Box>
);

const DocumentsTableCard: React.FC<{ listing: ReportGeneratorListingProps }> = ({ listing }) => {
  const {
    items,
    loading,
    error,
    isLoadingMore,
    appliedFiltersCount,
    onRefresh,
    onEndReached,
    onDownload,
    onRetry,
  } = listing;

  const maxHeight = reportGeneratorListLayout.mobileMaxHeight;
  const itemGap = reportGeneratorTheme.table.itemGap;

  const isListLoading = loading && !isLoadingMore;

  if (error) {
    return (
      <ErrorBoundary
        onRefresh={onRefresh}
        errorTitle="Não foi possível carregar o histórico"
        errorMessage="Tente novamente em alguns instantes."
      />
    );
  }

  if (isListLoading) {
    return (
      <DocumentsTableCardContainer>
        <DocumentsTableSkeletonList itemSeparatorSpace={itemGap} />
      </DocumentsTableCardContainer>
    );
  }

  return (
    <DocumentsTableCardContainer>
      <DocumentsTableList
        items={items}
        isLoadingMore={isLoadingMore}
        scrollEnabled
        onEndReached={onEndReached}
        listStyle={{
          maxHeight,
          backgroundColor: 'transparent',
        }}
        onDownload={onDownload}
        onRetry={onRetry}
        itemSeparatorSpace={itemGap}
        appliedFiltersCount={appliedFiltersCount}
        listBackground={false}
        listContentPadding={false}
        listContentFlexGrow={0}
      />
    </DocumentsTableCardContainer>
  );
};

export const DocumentsTable: React.FC<DocumentsTableProps> = ({ listing }) =>
  useBreakpointValue({
    base: <DocumentsTableCard listing={listing} />,
    sm: <DocumentsTableTableLayout listing={listing} />,
  });

export type { ReportGeneratorListingProps } from './types';
